<template>
  <div
    ref="appRoot"
    class="pc:flex pc:h-screen pc:overflow-hidden"
    style="font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif"
  >
    <UiOverlay :modelValue="isConnecting" class="connecting-overlay">
      <UiProgressCircular
        class="absolute top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 text-center"
        :size="140"
        color="#3ABBDB"
        indeterminate
      />
    </UiOverlay>

    <!-- Backdrop for the overlay sidebars (mobile/tablet only). Clicking it
         closes whichever sidebar is open. Rendered once, shared by both. -->
    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showMenu && isMobileLayout && (drawer || isPreviewOpen)"
        class="mobile:fixed mobile:inset-0 mobile:z-90 bg-gray-900/50 backdrop-blur-sm"
        @click="closeOverlays"
      />
    </transition>

    <ChapiMenu v-if="showMenu" v-model="drawer" />

    <main
      class="pc:flex-1 pc:overflow-y-auto pc:h-screen mobile:h-auto relative min-h-screen min-w-0 bg-gray-100"
    >
      <div
        v-if="isNavigating"
        class="pointer-events-none absolute top-0 right-0 left-0 z-50 h-1.5 overflow-hidden"
        aria-hidden="true"
      >
        <div class="animate-gradient h-full w-full" />
      </div>
      <div class="sticky top-0 left-0 z-55 h-0 w-0" v-if="showMenu">
        <div
          class="bg-teal absolute top-0 size-7 cursor-pointer overflow-hidden rounded-br-full"
          @click="setDrawer"
        >
          <UiIcon
            class="size-5 cursor-pointer text-white"
            :name="drawer ? 'chevron-left' : 'chevron-right'"
          />
        </div>
      </div>
      <Snackbar />
      <LeaveConfirmModal />
      <WarningBanner v-if="showMenu" />
      <PublicationModal v-if="showPublicationModal" />
      <router-view />
      <UiButton
        v-if="showMenu"
        variant="primary"
        class="fixed right-0 bottom-0 z-50 mr-4 mb-4 rounded-full transition-[transform] duration-300 ease-in-out"
        :class="{ '-translate-x-112.5': isPreviewOpen }"
        @click="togglePreview"
      >
        <UiIcon left :name="isPreviewOpen ? 'close' : 'smartphone'" />
        {{ isPreviewOpen ? $t('PREVIEW.CLOSE') : $t('PREVIEW.OPEN') }}
      </UiButton>
    </main>

    <PreviewSidebar v-if="showMenu" />
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import UiButton from '@/shared/components/ui/UiButton.vue'
import UiIcon from '@/shared/components/ui/UiIcon.vue'
import UiOverlay from '@/shared/components/ui/UiOverlay.vue'
import UiProgressCircular from '@/shared/components/ui/UiProgressCircular.vue'
import { useResize } from '@/shared/composables/useResize'
import { useRouterProgress } from '@/shared/composables/useRouterProgress'

import PublicationModal from './pages/publications/PublicationModal.vue'
import { usePublicationsStore } from './pages/publications/publications.store'
import { publicRoutes } from './router'
import ChapiMenu from './shared/components/ChapiMenu.vue'
import PreviewSidebar from './shared/components/preview/PreviewSidebar.vue'
import WarningBanner from './shared/components/WarningBanner.vue'
import LeaveConfirmModal from './shared/leaveConfirm/LeaveConfirmModal.vue'
import Snackbar from './shared/snackbar/SnackBar.vue'
import { useAppConfigStore } from './shared/store/app-config.store'
import { useAppDataStore } from './shared/store/app-data.store'
import { useIsMobileStore } from './shared/store/isMobile.store'
import { usePreviewStore } from './shared/store/preview.store'
import { useSessionStore } from './shared/store/session.store'

dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(timezone)
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.extend(localizedFormat)
dayjs.locale('fr')

const { isNavigating } = useRouterProgress()
const route = useRoute()

const appRoot = ref<HTMLElement | null>(null)
const drawer = ref(true)

const sessionStore = useSessionStore()
const isConnecting = computed(() => sessionStore.loading)
const isConnected = computed(() => sessionStore.principal != null)
const isMobileStore = useIsMobileStore()
const getIsSmallLaptop = computed(() => isMobileStore.isSmallLaptop)

const previewStore = usePreviewStore()
const isPreviewOpen = computed(() => previewStore.isOpen)
const publicationsStore = usePublicationsStore()
const showPublicationModal = computed(
  () => publicationsStore.showPublicationModal,
)
// True on mobile + tablet (< 1264px). Matches the `mobile` Tailwind variant
// in app.css and the `isSmallLaptop` flag in isMobile.store.
const isMobileLayout = computed(() => getIsSmallLaptop.value)

const showMenu = computed(
  () =>
    isConnected.value &&
    !isConnecting.value &&
    ![...publicRoutes, '/login'].includes(route.path),
)

function setDrawer() {
  // On mobile/tablet only one sidebar may be open at a time: opening the
  // menu closes the preview.
  if (isMobileLayout.value && !drawer.value && isPreviewOpen.value) {
    previewStore.setOpen(false)
  }
  drawer.value = !drawer.value
}

function togglePreview() {
  if (isMobileLayout.value && !isPreviewOpen.value && drawer.value) {
    drawer.value = false
  }
  previewStore.toggleOpen()
}

function closeOverlays() {
  if (drawer.value) drawer.value = false
  if (isPreviewOpen.value) previewStore.setOpen(false)
}

// Mobile/tablet: the menu is an overlay, close it once a page is chosen
watch(
  () => route.path,
  () => {
    if (isMobileLayout.value && drawer.value) drawer.value = false
  },
)

// When crossing the PC/mobile boundary, reset to the canonical default state
// for each layout: PC opens the menu (preview keeps its own state), mobile
// closes both so the user isn't left with a phantom backdrop.
watch(isMobileLayout, (mobile) => {
  if (!showMenu.value) return
  if (mobile) {
    drawer.value = false
    if (isPreviewOpen.value) previewStore.setOpen(false)
  } else {
    drawer.value = true
  }
})

onMounted(async () => {
  const appConfigStore = useAppConfigStore()
  watch(
    () => appConfigStore.appConfig?.timezone ?? 'Europe/Paris',
    (newValue: string) => {
      dayjs.tz.setDefault(newValue)
    },
    { immediate: true },
  )

  const { width, setTarget } = useResize()
  setTarget(appRoot.value)
  watch(
    () => width.value,
    (newWidth: number) => {
      if (newWidth < 769) {
        useIsMobileStore.getState().setIsMobile()
      } else if (newWidth < 1264) {
        useIsMobileStore.getState().setIsSmallLaptop()
      } else {
        useIsMobileStore.getState().setIsDefault()
      }
    },
    { immediate: true },
  )
})
</script>

<style scoped>
@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.animate-gradient {
  background: linear-gradient(
    90deg,
    var(--color-teal),
    var(--color-lightBlue),
    var(--color-teal)
  );
  background-size: 200% 200%;
  animation: gradient-animation 2s ease infinite;
}
</style>
